import * as React from 'react';
import { AudioLinesIcon, CircleStopIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { useCreateAgentConversation, useStartAgentVoice, useOperateAgentVoice, type AgentAccessScope, type AgentRecordContext } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { workspaceError } from '../workspace-utils';
import { startPcmCapture, type PcmCapture } from './pcm-capture';

type Phase = 'idle' | 'starting' | 'listening' | 'thinking' | 'speaking' | 'waiting';
export function likelyEcho(heard: string, spoken: string) {
  const clean = (text: string) => text.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  const a = clean(heard), b = clean(spoken);
  return a.length >= 8 && b.length >= 8 && (b.includes(a) || a.includes(b.slice(-Math.min(a.length, 60))));
}
export function speechChunks(text: string) {
  const plain = text.replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, '$1').replace(/https?:\/\/\S+/g, '').replace(/[*_`#>]/g, '');
  if (plain.length > 30000) throw new Error('This reply is too long for spoken playback. Read the complete reply in the chat.');
  return plain.match(/[\s\S]{1,900}(?:\s|$)|[\s\S]{1,900}/g)?.map(part => part.trim()).filter(Boolean) ?? [];
}
interface Props {
  beforeControls?: React.ReactNode;
  afterControls?: React.ReactNode;
  help?: React.ReactNode;
  scope: AgentAccessScope;
  context?: AgentRecordContext;
  conversationId?: string;
  disabled: boolean;
  blocked: boolean;
  onConversation: (id: string) => void;
  onUtterance: (text: string) => Promise<string | null>;
  onInterrupt: () => void;
  onActiveChange: (active: boolean) => void;
  stopRef: React.MutableRefObject<(() => void) | null>;
}
export function AgentVoice({ scope, context, conversationId, disabled, blocked, onConversation, onUtterance, onInterrupt, onActiveChange, stopRef, beforeControls, afterControls, help }: Props) {
  const [phase, setPhase] = React.useState<Phase>('idle');
  const helpId = React.useId();
  const [muted, setMuted] = React.useState(false);
  const [transcript, setTranscript] = React.useState('');
  const [error, setError] = React.useState('');
  const pendingUtterance = React.useRef<(() => void) | null>(null);
  const owner = React.useRef({ epoch: 0, turn: 0, phase: 'idle' as Phase, muted: false, sessionId: '', controller: new AbortController(),
    playback: new AbortController(), capture: null as PcmCapture | null, audio: null as HTMLAudioElement | null, url: '',
    timer: undefined as ReturnType<typeof setTimeout> | undefined, spoken: '', lastFinal: '', lastFinalAt: 0, spokenAt: 0, thread: conversationId });
  const callbacks = React.useRef({ onConversation, onUtterance, onInterrupt, onActiveChange });
  React.useEffect(() => { callbacks.current = { onConversation, onUtterance, onInterrupt, onActiveChange }; }, [onConversation, onUtterance, onInterrupt, onActiveChange]);
  const operationSignal = React.useRef<AbortSignal | undefined>(undefined);
  const create = useCreateAgentConversation({ mutation: { retry: false }, request: { get signal() { return owner.current.controller.signal; } } });
  const start = useStartAgentVoice({ mutation: { retry: false }, request: { get signal() { return owner.current.controller.signal; } } });
  const operation = useOperateAgentVoice({ mutation: { retry: false }, request: { get signal() { return operationSignal.current; } } });
  const setState = (next: Phase) => { owner.current.phase = next; setPhase(next); };
  const stopPlayback = () => {
    const current = owner.current;
    current.playback.abort(); current.spokenAt = Date.now(); current.audio?.pause(); current.audio = null;
    if (current.url) URL.revokeObjectURL(current.url);
    current.url = '';
  };
  const stop = React.useCallback(() => {
    const current = owner.current;
    current.epoch++; current.turn++;
    operationSignal.current = undefined; pendingUtterance.current = null;
    if (current.phase === 'thinking') callbacks.current.onInterrupt();
    current.controller.abort(); current.capture?.stop(); current.capture = null;
    stopPlayback(); clearTimeout(current.timer);
    const sessionId = current.sessionId; current.sessionId = '';
    if (sessionId) operation.mutate({ data: { accessScope: scope, context, sessionId, conversationId: current.thread, requestId: crypto.randomUUID(), operation: 'end' } });
    current.phase = 'idle'; setPhase('idle'); callbacks.current.onActiveChange(false);
  }, [operation.mutate, scope.kind, scope.tenantId, scope.entityId, context?.kind, context?.tenantId, context?.entityId, context?.recordId]);
  React.useEffect(() => {
    stopRef.current = stop;
    return () => { stop(); stopRef.current = null; };
  }, [stop, stopRef]);
  React.useEffect(() => {
    const current = owner.current;
    if (current.thread !== conversationId) { if (current.thread) stop(); current.thread = conversationId; }
  }, [conversationId, stop]);
  React.useEffect(() => { if (blocked) stop(); }, [blocked, stop]);
  function fail(message: string) { stop(); setError(message); }
  async function play(text: string, epoch: number, turn: number) {
    const current = owner.current;
    const playback = new AbortController(); current.playback = playback;
    const signal = AbortSignal.any([current.controller.signal, playback.signal]);
    for (const part of speechChunks(text)) {
      if (current.epoch !== epoch || current.turn !== turn || current.muted || signal.aborted) return;
      current.spoken = part; current.spokenAt = Date.now();
      setState('speaking');
      operationSignal.current = signal;
      const result = await operation.mutateAsync({ data: { accessScope: scope, context, sessionId: current.sessionId,
        requestId: crypto.randomUUID(), operation: 'speak', text: part } }, { });
      if (current.epoch !== epoch || current.turn !== turn || current.muted || signal.aborted) return;
      if (!result.audioBase64) throw new Error('Voice returned no audio.');
      const bytes = Uint8Array.from(atob(result.audioBase64), character => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
      current.url = url;
      const audio = new Audio(url); current.audio = audio;
      try {
        await new Promise<void>((resolve, reject) => {
          const finish = (failure?: Error) => { audio.onended = null; audio.onerror = null; signal.removeEventListener('abort', aborted); failure ? reject(failure) : resolve(); };
          const aborted = () => { audio.pause(); finish(); };
          audio.onended = () => finish(); audio.onerror = () => finish(new Error('Spoken reply could not be played. Continue in text.'));
          signal.addEventListener('abort', aborted, { once: true });
          void audio.play().catch(() => finish(new Error('Browser blocked playback. Start voice in its own browser tab.')));
        });
      } finally {
        audio.pause(); if (current.audio === audio) current.audio = null;
        if (current.url === url) { URL.revokeObjectURL(url); current.url = ''; }
      }
    }
  }
  async function begin() {
    if (owner.current.phase !== 'idle' || disabled || blocked || (context && !conversationId)) return;
    const current = owner.current, epoch = ++current.epoch;
    current.controller = new AbortController(); current.lastFinal = ''; current.spoken = '';
    setError(''); setTranscript(''); setState('starting'); callbacks.current.onActiveChange(true);
    try {
      const id = conversationId ?? (await create.mutateAsync({ data: { accessScope: scope, title: 'Voice chat', mode: 'internal' } })).id;
      if (epoch !== current.epoch) return;
      current.thread = id; callbacks.current.onConversation(id);
      const admissionId = crypto.randomUUID(); current.sessionId = admissionId;
      const admission = await start.mutateAsync({ data: { accessScope: scope, context, conversationId: id, requestId: admissionId } });
      if (epoch !== current.epoch) {
        operation.mutate({ data: { accessScope: scope, context, sessionId: admission.sessionId, conversationId: id, requestId: crypto.randomUUID(), operation: 'end' } });
        return;
      }
      current.sessionId = admission.sessionId;
      // A null admission has no quota timer. Rearm long durations within browser timer bounds.
      const expiresAt = admission.expiresAt === null ? null : Date.parse(admission.expiresAt);
      const checkExpiry = () => {
        if (current.epoch !== epoch || expiresAt === null) return;
        const remaining = expiresAt - Date.now();
        if (remaining <= 0) fail('Voice reached its configured session duration. Start voice again or continue in text.');
        else current.timer = setTimeout(checkExpiry, Math.min(remaining, 2147483647));
      };
      checkExpiry();
      if (current.epoch !== epoch) return;
      const submitUtterance = (audioBase64: string) => {
        if (current.epoch !== epoch || current.phase !== 'listening') return;
        setState('thinking');
        const turn = ++current.turn;
        operationSignal.current = current.controller.signal;
        void (async () => {
          const result = await operation.mutateAsync({ data: { accessScope: scope, context, sessionId: current.sessionId,
            requestId: crypto.randomUUID(), operation: 'transcribe', audioBase64 } });
          if (current.epoch !== epoch) return;
          const text = result.text?.trim();
          if (!text || (text === current.lastFinal && Date.now() - current.lastFinalAt < 1500) || (Date.now() - current.spokenAt < 15000 && likelyEcho(text, current.spoken))) { setState('listening'); return; }
          current.lastFinal = text; current.lastFinalAt = Date.now(); setTranscript(text);
          const reply = await callbacks.current.onUtterance(text);
          if (current.epoch !== epoch) return;
          if (reply === null) { fail('Spoken message has no confirmed outcome. Check the send outcome in the chat; it will not be replayed.'); return; }
          if (!current.muted) await play(reply, epoch, turn);
          if (current.epoch === epoch && current.turn === turn) setState('listening');
        })().catch(reason => {
          if (current.epoch !== epoch || current.turn !== turn) return;
          if (reason && typeof reason === 'object' && 'code' in reason && reason.code === 'AGENT_VOICE_BUSY') {
            pendingUtterance.current = () => { pendingUtterance.current = null; setError(''); setState('listening'); submitUtterance(audioBase64); };
            setState('waiting');
            setError('The previous reply is still stopping. Your captured message has not been sent. Continue it explicitly when ready.');
          } else fail(workspaceError(reason));
        });
      };
      current.capture = await startPcmCapture(current.controller.signal, () => {
        if (current.epoch !== epoch) return;
        if (current.phase === 'speaking') { current.turn++; stopPlayback(); setState('listening'); }
        else if (current.phase === 'thinking') fail('Interrupted. Check the send outcome in the chat before sending another request.');
      }, submitUtterance, message => { if (current.epoch === epoch) fail(message); });
      if (current.epoch !== epoch) { current.capture.stop(); return; }
      setState('listening');
    } catch (reason) { if (current.epoch === epoch) fail(workspaceError(reason)); }
  }
  return <section className="space-y-1 text-xs" aria-label="Interactive voice">
    <div role="group" aria-label="Agent voice controls" className="flex min-w-0 items-center gap-1 overflow-x-auto pb-1">
      {beforeControls}
      {phase === 'idle' ? <Button type="button" variant="outline" size="icon" className="size-8" aria-label="Start voice" title="Start voice conversation · audio sent to the configured speech service" aria-describedby={helpId} disabled={disabled || blocked} onClick={() => void begin()}><AudioLinesIcon className="size-4" /></Button>
        : <Button type="button" variant="secondary" size="icon" className="size-8" aria-label="End voice" title="End voice conversation" onClick={stop}><CircleStopIcon className="size-4" /></Button>}
      {phase !== 'idle' && <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={muted ? 'Unmute spoken replies' : 'Mute spoken replies'} title={muted ? 'Unmute voice conversation' : 'Mute voice conversation'} aria-pressed={muted} onClick={() => {
        owner.current.muted = !owner.current.muted; setMuted(owner.current.muted);
        if (owner.current.muted) { if (owner.current.phase === 'speaking') { owner.current.turn++; setState('listening'); } stopPlayback(); }
      }}>{muted ? <VolumeXIcon className="size-4" /> : <Volume2Icon className="size-4" />}</Button>}
      {afterControls}
    </div>
    {phase !== 'idle' && <p role="status">{phase === 'waiting' ? 'Captured message waiting' : phase === 'starting' ? 'Connecting microphone…' : phase === 'listening' ? 'Listening…' : phase === 'thinking' ? 'Thinking…' : 'Speaking… you can interrupt'}</p>}
    {phase === 'waiting' && <Button type="button" variant="outline" size="sm" onClick={() => pendingUtterance.current?.()}>Continue captured message</Button>}
    <div className="sr-only">
      <p id={helpId}>Start voice sends microphone audio and replies to the speech service your administrator configured for this app. Spoken requests use this chat; changes still require exact review. Platform administrators may configure session allowances. Sessions close after 30 minutes without speech activity.</p>
      {help}
    </div>
    {transcript && <p aria-live="polite">You said: {transcript}</p>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
